// ============================================================================
// SECTION 1: IMPORTS & SCHEMAS
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StudentOnboarding from './StudentOnboarding'; 
import BookingCalendar from './BookingCalendar';
import StudentProgressReport from './StudentProgressReport';

import { DYNAMIC_VEHICLE_FRAMEWORKS, MASTER_READINESS_CHECKLIST, scoringGuide } from './constants';
import * as Styles from './styles';
import LearnerHelpModal from './LearnerHelpModal';

// ============================================================================
// SECTION 2: COMPONENT INITIALIZATION & STATE (WITH AUTO MOBILE DETECTION)
// ============================================================================
function LearnerDash() {
  const { instructorId } = useParams();
  const navigate = useNavigate();
  
  // Detect if screen width is mobile (< 768px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // -- General Data State --
  const [instructor, setInstructor] = useState(null);
  const [template, setTemplate] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [studentHistoricalLogs, setStudentHistoricalLogs] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);

  // -- UI & Interaction State --
  const [activeMainTab, setActiveMainTab] = useState('grid'); // Desktop tab ('grid', 'recurring', 'hub')
  const [mobileTab, setMobileTab] = useState('book'); // Mobile tab ('book', 'bulk', 'progress', 'chat')
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedAuditLogId, setSelectedAuditLogId] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // -- Booking / Cart State --
  const [stagedBookings, setStagedBookings] = useState([]);
  const [recurringDay, setRecurringDay] = useState('1'); 
  const [recurringStartTime, setRecurringStartTime] = useState('10:00');
  const [recurringEndTime, setRecurringEndTime] = useState('14:00');
  const [recurringWeeks, setRecurringWeeks] = useState('4'); 

  // -- Resolved IDs --
  const sessionLearnerId = localStorage.getItem('learnerId') || localStorage.getItem('userId');
  const sessionEmail = localStorage.getItem('userEmail') || localStorage.getItem('learnerEmail');
  const learnerName = localStorage.getItem('userName') || "Learner Driver";
  const targetInstructorId = instructorId || localStorage.getItem('instructorId') || 5;
  const timeLabels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
  const [learnerId, setLearnerId] = useState(sessionLearnerId || 1);

  // -- Messaging State --
  const [messagesList, setMessagesList] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');

  const fetchMessages = useCallback(async () => {
    if (!targetInstructorId || !learnerId) return;
    try {
      const res = await fetch(`/api/messages/${targetInstructorId}/${learnerId}`);
      if (res.ok) {
        const data = await res.json();
        const sorted = Array.isArray(data) ? data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) : [];
        setMessagesList(sorted);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  }, [targetInstructorId, learnerId]);

  useEffect(() => {
    if (targetInstructorId && learnerId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [targetInstructorId, learnerId, fetchMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessageText.trim()) return;

    try {
        const res = await fetch('/api/messages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                instructor_id: parseInt(targetInstructorId, 10),
                learner_id: parseInt(learnerId, 10),
                sender_role: 'learner',
                message_text: newMessageText.trim()
            })
        });

        if (res.ok) {
            setNewMessageText('');
            fetchMessages();
        }
    } catch (err) {
        console.error("Error sending message:", err);
    }
  };

  // ============================================================================
  // SECTION 3: ROBUST DATA FETCHING
  // ============================================================================
  const fetchScheduleData = useCallback(async () => {
    if (!targetInstructorId) return;
    try {
      const iRes = await fetch(`/api/instructors/${targetInstructorId}`);
      if (iRes.ok) {
        const iData = await iRes.json();
        setInstructor(iData);
        if (iData.calendar_template) {
          const parsed = typeof iData.calendar_template === 'string' ? JSON.parse(iData.calendar_template) : iData.calendar_template;
          setTemplate(Array.isArray(parsed) ? parsed : []);
        } else {
          setTemplate([]);
        }
      }

      const bRes = await fetch(`/api/bookings?instructorId=${targetInstructorId}`);
      if (bRes.ok) {
        const bData = await bRes.json();
        setExistingBookings(Array.isArray(bData) ? bData : []);
      }

      const learnersRes = await fetch(`/api/learners?instructorId=${targetInstructorId}`);
      if (learnersRes.ok) {
        const learnersData = await learnersRes.json();
        let currentMe = learnersData.find(l => 
          (sessionLearnerId && parseInt(l.id, 10) === parseInt(sessionLearnerId, 10)) ||
          (sessionEmail && l.email && l.email.toLowerCase() === sessionEmail.toLowerCase())
        );

        if (!currentMe && learnersData.length > 0) currentMe = learnersData[0];

        if (currentMe) {
          setLearnerId(currentMe.id);
          setStudentProfile({
            ...currentMe,
            training_category: currentMe.training_category || "Car (Manual)", 
            skills: typeof currentMe.skills === 'string' ? JSON.parse(currentMe.skills) : (currentMe.skills || {}),
            test_readiness_checklist: typeof currentMe.test_readiness_checklist === 'string' ? JSON.parse(currentMe.test_readiness_checklist) : (currentMe.test_readiness_checklist || [])
          });

          const logsRes = await fetch(`/api/lesson-logs/${currentMe.id}`);
          if (logsRes.ok) {
            const logsData = await logsRes.json();
            const sortedLogs = Array.isArray(logsData) ? logsData.sort((a, b) => new Date(b.lesson_date) - new Date(a.lesson_date)) : [];
            setStudentHistoricalLogs(sortedLogs);
            if (sortedLogs.length > 0 && !selectedAuditLogId) {
              setSelectedAuditLogId(String(sortedLogs[0].id));
            }
          }
        }
      }
    } catch (err) {
      console.error("Dependency Sync Matrix Crash:", err);
    }
  }, [targetInstructorId, sessionLearnerId, sessionEmail, selectedAuditLogId]);

  useEffect(() => {
    fetchScheduleData();
  }, [fetchScheduleData, currentWeekStart]);

  useEffect(() => {
    const shouldForceOnboarding = localStorage.getItem('forceOnboarding');
    if (shouldForceOnboarding === 'true') {
      setShowSettings(true);
    }
  }, []);

  // ============================================================================
  // SECTION 4: PROGRESS & DONUT CHART CALCULATIONS
  // ============================================================================
  const calculateStudentProgress = () => {
    const categoryString = studentProfile?.training_category || "Car (Manual)";
    const curriculumFramework = DYNAMIC_VEHICLE_FRAMEWORKS[categoryString];
    if (!curriculumFramework) return 0;

    const totalSkillsInCurriculum = Object.values(curriculumFramework).flat().length;
    const studentSkillsMap = studentProfile?.skills || {};
    const totalAccumulatedScore = Object.values(studentSkillsMap).reduce((sum, current) => sum + current, 0);
    const maxPossiblePoints = totalSkillsInCurriculum * 5;

    return Math.round((totalAccumulatedScore / maxPossiblePoints) * 100) || 0;
  };

  const progressPercent = calculateStudentProgress();

  const getTrafficLightColor = (percent) => {
      if (percent >= 80) return '#22c55e';
      if (percent >= 50) return '#eab308';
      return '#ef4444';
  };

  const getDonutSvg = (percent, size = 80, stroke = 8) => {
      const radius = 34;
      const circumference = 2 * Math.PI * radius;
      const strokeDashoffset = circumference - (percent / 100) * circumference;
      const color = getTrafficLightColor(percent);

      return `
          <svg width="${size}" height="${size}" viewBox="0 0 100 100" style="transform: rotate(-90deg);">
              <circle cx="50" cy="50" r="${radius}" stroke="#e2e8f0" stroke-width="${stroke}" fill="transparent" />
              <circle cx="50" cy="50" r="${radius}" stroke="${color}" stroke-width="${stroke}" stroke-dasharray="${circumference}" stroke-dashoffset="${strokeDashoffset}" stroke-linecap="round" fill="transparent" />
          </svg>
      `;
  };

  // ============================================================================
  // SECTION 5: BOOKING ACTIONS
  // ============================================================================
  const handleRecurringSubmit = (e) => {
    e.preventDefault();
    const startHour = parseInt(recurringStartTime.split(':')[0], 10);
    const endHour = parseInt(recurringEndTime.split(':')[0], 10);

    const instStart = parseInt(instructor?.work_start_time?.substring(0, 2) || 0, 10);
    const instEnd = parseInt(instructor?.work_end_time?.substring(0, 2) || 24, 10);
    if (startHour < instStart || endHour > instEnd) {
        alert(`❌ Instructor is only available between ${instructor?.work_start_time || '08:00'} and ${instructor?.work_end_time || '18:00'}`);
        return;
    }

    if (startHour >= endHour) {
        alert("❌ End time must be later than the start time!");
        return;
    }

    const targetDayIndex = parseInt(recurringDay, 10); 
    const totalWeeks = parseInt(recurringWeeks, 10);
    let tempStage = [];
    
    let today = new Date();
    today.setHours(0, 0, 0, 0); 

    for (let i = 0; i < totalWeeks * 7; i++) {
        let lookDate = new Date();
        lookDate.setDate(today.getDate() + i);
        if (lookDate < today) continue;

        let currentDayIndex = lookDate.getDay() || 7; 
        if (currentDayIndex === targetDayIndex) {
            const isoDateStr = lookDate.toISOString().slice(0, 10);
            const duration = endHour - startHour;
            const hourStr = `${startHour.toString().padStart(2, '0')}:00`;

            tempStage.push({
                id: Math.random(), 
                selected: true,     
                dateString: isoDateStr,
                formattedDate: lookDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
                time: hourStr,
                duration: duration
            });
        }
    }
    setStagedBookings(tempStage);
    alert(`Generated ${tempStage.length} slots. Review and submit below!`);
  };

  const toggleStagedItemSelection = (id) => {
    setStagedBookings(prev => prev.map(item => item.id === id ? { ...item, selected: !item.selected } : item));
  };

  const commitAllStagedBookings = async () => {
    const activeStaged = stagedBookings.filter(b => b.selected);
    if (activeStaged.length === 0) { alert("❌ No slots selected."); return; }
    if (!window.confirm(`Commit ${activeStaged.length} lesson blocks?`)) return;

    setIsBooking(true);
    try {
      const promises = activeStaged.map(bSlot => 
        fetch('/api/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instructor_id: parseInt(targetInstructorId, 10),
            learner_id: parseInt(learnerId, 10),
            learner_name: learnerName,
            booking_date: bSlot.dateString,
            booking_time: bSlot.time,
            duration: bSlot.duration 
          })
        })
      );
      await Promise.all(promises);
      alert(`🎉 Success! All ${activeStaged.length} blocks booked.`);
      setStagedBookings([]); 
      fetchScheduleData();
      if (isMobile) setMobileTab('book');
    } catch (err) { alert("❌ Failed to commit bookings."); }
    finally { setIsBooking(false); }
  };

  const handleCancelLesson = async (bookingId, dateStr, timeStr) => {
    if (!window.confirm(`Cancel lesson on ${dateStr} at ${timeStr}?`)) return;
    const response = await fetch(`/api/bookings/${bookingId}`, { method: 'DELETE' });
    if (response.ok) { alert("🗑️ Lesson canceled."); fetchScheduleData(); }
  };

  const getWeekDays = (start) => {
    const days = [];
    const tempDate = new Date(start);
    const day = tempDate.getDay();
    const diff = tempDate.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(tempDate.setDate(diff));
    for (let i = 0; i < 7; i++) { days.push(new Date(monday)); monday.setDate(monday.getDate() + 1); }
    return days;
  };

  const weekDates = getWeekDays(currentWeekStart);
  const myPersonalBookings = existingBookings.filter(b => parseInt(b.learner_id, 10) === parseInt(learnerId, 10));

  if (!instructor) return <div style={{ color: '#111', padding: '50px', textAlign: 'center', background: '#fff', minHeight: '100vh', fontWeight: 'bold' }}>Loading Portal...</div>;

 


// ============================================================================
  // SECTION 6: AUTOMATIC MOBILE RENDER (DARK-THEMED TILE & MENU FLOW)
  // ============================================================================
  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', color: '#ffffff', fontFamily: 'inherit', paddingBottom: '50px', boxSizing: 'border-box' }}>
        
        {/* Minimalist App Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#090d16', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 1000 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div>
              <div style={{ color: '#fff', fontWeight: '800', fontSize: '1.05rem', lineHeight: '1.2' }}>LessonLane</div>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', lineHeight: '1.2' }}>Portal: {learnerName}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => setShowSettings(true)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: '0.75rem', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>⚙️ Settings</button>
            <button onClick={() => { localStorage.clear(); navigate('/'); }} style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', color: '#fca5a5', fontSize: '0.75rem', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Logout</button>
          </div>
        </div>

        {/* Back Button (Shown when inside a specific tab) */}
        {mobileTab !== 'menu' && (
          <div style={{ padding: '14px 16px 0 16px', maxWidth: '500px', margin: '0 auto' }}>
            <button 
              onClick={() => setMobileTab('menu')}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#ffffff',
                padding: '8px 14px',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '0.8rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ← Back to Menu
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <div style={{ padding: '16px', maxWidth: '500px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Welcome Greeting Banner (Shown only on menu view) */}
          {mobileTab === 'menu' && (
            <div style={{ background: '#162238', padding: '16px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
              <h4 style={{ margin: '0 0 4px 0', color: '#ffffff', fontSize: '1rem', fontWeight: '800' }}>Welcome back, {learnerName} 👋</h4>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.4' }}>Manage your lesson schedules, bulk packages, and progress below.</p>
            </div>
          )}

          {/* 4 Sharp Gradient Tiles (Shown only on menu view) */}
          {mobileTab === 'menu' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              
              {/* Tile 1: Weekly Calendar */}
              <div 
                onClick={() => setMobileTab('book')}
                style={{ 
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 
                  padding: '18px 14px', 
                  borderRadius: '16px', 
                  textAlign: 'center', 
                  boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)', 
                  border: '1px solid rgba(255,255,255,0.2)', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  minHeight: '110px'
                }}
              >
                <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>📅</div>
                <div>
                  <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Weekly Calendar</div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Book & manage slots</div>
                </div>
              </div>

              {/* Tile 2: Month Bulk Plan */}
              <div 
                onClick={() => setMobileTab('bulk')}
                style={{ 
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', 
                  padding: '18px 14px', 
                  borderRadius: '16px', 
                  textAlign: 'center', 
                  boxShadow: '0 8px 20px rgba(5, 150, 105, 0.3)', 
                  border: '1px solid rgba(255,255,255,0.2)', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  minHeight: '110px'
                }}
              >
                <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>🔄</div>
                <div>
                  <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Month Bulk Plan</div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Recurring packages</div>
                </div>
              </div>

              {/* Tile 3: Progress Hub */}
              <div 
                onClick={() => setMobileTab('progress')}
                style={{ 
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', 
                  padding: '18px 14px', 
                  borderRadius: '16px', 
                  textAlign: 'center', 
                  boxShadow: '0 8px 20px rgba(124, 58, 237, 0.3)', 
                  border: '1px solid rgba(255,255,255,0.2)', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  minHeight: '110px'
                }}
              >
                <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>📊</div>
                <div>
                  <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Progress Hub</div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Competency matrix</div>
                </div>
              </div>

              {/* Tile 4: Direct Chat */}
              <div 
                onClick={() => setMobileTab('chat')}
                style={{ 
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', 
                  padding: '18px 14px', 
                  borderRadius: '16px', 
                  textAlign: 'center', 
                  boxShadow: '0 8px 20px rgba(217, 119, 6, 0.3)', 
                  border: '1px solid rgba(255,255,255,0.2)', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  minHeight: '110px'
                }}
              >
                <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>💬</div>
                <div>
                  <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Direct Chat</div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Instructor inbox</div>
                </div>
              </div>

            </div>
          )}

          {/* Active Tab Content Container */}
          {mobileTab !== 'menu' && (
            <div style={{ background: '#162238', padding: '18px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)' }}>
              
              {/* Booking Tab */}
              {mobileTab === 'book' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#94a3b8' }}>📅 Tap slot to book / cancel</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(new Date(d)); }} style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '6px' }}>◀ Prev</button>
                      <button onClick={() => setCurrentWeekStart(new Date())} style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '6px' }}>Today</button>
                      <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(new Date(d)); }} style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '6px' }}>Next ▶</button>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <BookingCalendar 
                      instructor={targetInstructorId} template={template} existingBookings={existingBookings} learnerId={learnerId} learnerName={learnerName} role="learner"
                      workStart={instructor?.work_start_time?.substring(0, 5) || "08:00"} workEnd={instructor?.work_end_time?.substring(0, 5) || "18:00"}
                      timeLabels={timeLabels} weekDates={weekDates} onBookingSuccess={fetchScheduleData}
                      onCellClick={(date, time, isStudentReserved, liveBooking) => {
                        const dKey = date.toDateString();
                        const isMyOwn = myPersonalBookings.some(b => {
                          const bDateStr = new Date(b.booking_date).toDateString();
                          return (bDateStr === dKey || b.booking_date === dKey) && b.booking_time.substring(0, 5) === time;
                        });
                        if (isMyOwn && liveBooking) handleCancelLesson(liveBooking.id, dKey, time);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Bulk Plan Tab */}
              {mobileTab === 'bulk' && (
                <div>
                  <h3 style={{ marginTop: 0, color: '#60a5fa', fontSize: '1.1rem', fontWeight: '800', marginBottom: '12px' }}>🔄 Setup Bulk Plan</h3>
                  <form onSubmit={handleRecurringSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.8rem' }}>
                    <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>📅 DAY OF THE WEEK</label>
                    <select value={recurringDay} onChange={e => setRecurringDay(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px' }}>
                      <option value="1">Every Monday</option><option value="2">Every Tuesday</option><option value="3">Every Wednesday</option><option value="4">Every Thursday</option><option value="5">Every Friday</option><option value="6">Every Saturday</option><option value="7">Every Sunday</option>
                    </select>
                    <button type="submit" style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', marginTop: '6px', cursor: 'pointer' }}>Generate Preview 📋</button>
                  </form>
                </div>
              )}

              {/* Direct Chat Tab */}
              {mobileTab === 'chat' && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#60a5fa', fontSize: '1.1rem', fontWeight: '800' }}>💬 Direct Chat with Instructor</h3>
                  <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" value={newMessageText} onChange={e => setNewMessageText(e.target.value)} placeholder="Type message..." style={{ flex: 1, padding: '11px 13px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }} />
                    <button type="submit" style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '11px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>Send 🚀</button>
                  </form>
                </div>
              )}

            </div>
          )}

        </div>

        {/* Settings Modal */}
        {showSettings && (
          <div style={Styles.modalOverlay}>
            <div style={{ ...Styles.modalContent, width: '90%', maxWidth: '400px', maxHeight: '85vh', overflowY: 'auto', background: '#162238', color: '#fff', padding: '20px', borderRadius: '16px', border: '1.5px solid #3b82f6' }}>
              <h3 style={{ color: '#60a5fa', marginTop: 0 }}>Account Settings</h3>
              <StudentOnboarding studentId={learnerId} onComplete={() => { setShowSettings(false); fetchScheduleData(); }} />
              <button onClick={() => setShowSettings(false)} style={{ background: '#334155', color: '#fff', width: '100%', marginTop: '12px', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}

        <LearnerHelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
      </div>
    );
  }



  // ============================================================================
  // SECTION 7: DESKTOP RENDER (>= 768px)
  // ============================================================================
  return (
    <div style={{ ...Styles.containerStyle, backgroundColor: '#ffffff', color: '#111111' }}>
      
      {/* --- HEADER --- */}
      <div style={{ ...Styles.headerStyle, backgroundColor: '#f8f9fa', borderBottom: '2px solid #e2e8f0' }}>
        <div>
          <h2 style={{ color: '#111111', margin: 0, fontWeight: '800' }}>Portal: Booking with {instructor.name}</h2>
          <span style={{ color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>Welcome back, {learnerName}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowHelpModal(true)} style={{ ...Styles.navBtn, background: '#ffffff', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold' }}>📖 Help Guide</button>
          <button onClick={() => setShowSettings(true)} style={{ ...Styles.navBtn, background: '#ffffff', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold' }}>⚙️ Settings</button>
          <button onClick={() => { localStorage.clear(); sessionStorage.clear(); navigate('/'); }} style={{ ...Styles.navBtn, backgroundColor: '#e53e3e', color: '#ffffff', fontWeight: 'bold', border: 'none' }}>Logout 🚪</button>
        </div>
      </div>

      {/* --- TOP NAVIGATION TABS --- */}
      <div style={{ ...Styles.tabBar, backgroundColor: '#f8f9fa', borderBottom: '1px solid #cbd5e0', padding: '12px 20px', display: 'flex', gap: '10px' }}>
        <button 
          onClick={() => setActiveMainTab('grid')} 
          style={activeMainTab === 'grid' ? { background: '#0066cc', color: '#ffffff', borderRadius: '6px', border: 'none', fontWeight: 'bold', padding: '8px 16px' } : { background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0', borderRadius: '6px', fontWeight: 'bold', padding: '8px 16px' }}
        >
          📅 Weekly Calendar Grid
        </button>
        <button 
          onClick={() => setActiveMainTab('recurring')} 
          style={activeMainTab === 'recurring' ? { background: '#0066cc', color: '#ffffff', borderRadius: '6px', border: 'none', fontWeight: 'bold', padding: '8px 16px' } : { background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0', borderRadius: '6px', fontWeight: 'bold', padding: '8px 16px' }}
        >
          🔄 Month Bulk Booking
        </button>
        <button 
          onClick={() => setActiveMainTab('hub')} 
          style={activeMainTab === 'hub' ? { background: '#0066cc', color: '#ffffff', borderRadius: '6px', border: 'none', fontWeight: 'bold', padding: '8px 16px' } : { background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0', borderRadius: '6px', fontWeight: 'bold', padding: '8px 16px' }}
        >
          🎯 Progress & Reporting Hub ({myPersonalBookings.length} Bookings)
        </button>
      </div>

      {/* --- TAB 1: WEEKLY CALENDAR GRID & SIDEBAR CHAT --- */}
      {activeMainTab === 'grid' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <p style={{ margin: 0, color: '#4a5568', fontWeight: '500' }}>💡 Click any available slot to book, or click your own lesson to cancel it directly.</p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(new Date(d)); }} style={{ ...Styles.navBtn, background: '#f8f9fa', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold', padding: '6px 12px', cursor: 'pointer', borderRadius: '6px' }}>◀ Prev</button>
              <button onClick={() => setCurrentWeekStart(new Date())} style={{ ...Styles.navBtn, background: '#f8f9fa', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold', padding: '6px 12px', cursor: 'pointer', borderRadius: '6px' }}>Today</button>
              <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(new Date(d)); }} style={{ ...Styles.navBtn, background: '#f8f9fa', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold', padding: '6px 12px', cursor: 'pointer', borderRadius: '6px' }}>Next ▶</button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '350px' }}>
              <BookingCalendar 
                instructor={targetInstructorId}
                template={template}
                existingBookings={existingBookings}
                learnerId={learnerId}
                learnerName={learnerName}
                role="learner"
                workStart={instructor?.work_start_time?.substring(0, 5) || "08:00"}
                workEnd={instructor?.work_end_time?.substring(0, 5) || "18:00"}
                timeLabels={timeLabels}
                weekDates={weekDates}
                onBookingSuccess={fetchScheduleData}
                onCellClick={(date, time, isStudentReserved, liveBooking) => {
                  const dKey = date.toDateString();
                  const isMyOwnBooking = myPersonalBookings.some(b => {
                      const bDateStr = new Date(b.booking_date).toDateString();
                      return (bDateStr === dKey || b.booking_date === dKey) && b.booking_time.substring(0, 5) === time;
                  });
                  if (isMyOwnBooking && liveBooking) handleCancelLesson(liveBooking.id, dKey, time);
                }}
              />
            </div>

            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', width: '360px', flexShrink: 0 }}>
              <h3 style={{ marginTop: 0, color: '#0066cc', fontSize: '15px' }}>💬 Direct Chat with Instructor</h3>
              <p style={{ fontSize: '11px', color: '#4a5568', marginBottom: '12px' }}>Instant updates and schedule changes.</p>
              
              <div style={{ height: '350px', overflowY: 'auto', background: '#f8f9fa', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                {messagesList.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#64748b', marginTop: '120px', fontSize: '12px' }}>No messages yet. Send a message below to start!</div>
                ) : (
                  messagesList.map(msg => {
                    const isMe = msg.sender_role === 'learner';
                    return (
                      <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                        <div style={{ background: isMe ? '#0066cc' : '#ffffff', color: isMe ? '#ffffff' : '#111111', padding: '8px 12px', borderRadius: '8px', border: isMe ? 'none' : '1px solid #cbd5e0', fontSize: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          {msg.message_text}
                        </div>
                        <span style={{ fontSize: '9px', color: '#64748b', marginTop: '2px', display: 'block', textAlign: isMe ? 'right' : 'left' }}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  value={newMessageText} 
                  onChange={e => setNewMessageText(e.target.value)} 
                  placeholder="Type message..." 
                  style={{ flex: 1, padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', background: '#ffffff', color: '#111111', fontSize: '12px' }}
                />
                <button type="submit" style={{ background: '#0066cc', color: '#ffffff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>Send 🚀</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- TAB 2: RECURRING --- */}
      {activeMainTab === 'recurring' && (
        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', padding: '20px' }}>
          <div style={{ ...Styles.cardStyle, flex: 1, minWidth: '350px', background: '#ffffff', border: '1px solid #cbd5e0' }}>
            <h3 style={{ marginTop: 0, color: '#0066cc' }}>🔄 Setup Bulk Plan</h3>
            <form onSubmit={handleRecurringSubmit} style={Styles.formStyle}>
              <div style={Styles.formGroup}>
                <label style={{ ...Styles.labelStyle, color: '#111111', fontWeight: 'bold' }}>📅 Day of the Week</label>
                <select value={recurringDay} onChange={(e) => setRecurringDay(e.target.value)} style={{ ...Styles.inputStyle, background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0' }}>
                  <option value="1">Every Monday</option>
                  <option value="2">Every Tuesday</option>
                  <option value="3">Every Wednesday</option>
                  <option value="4">Every Thursday</option>
                  <option value="5">Every Friday</option>
                  <option value="6">Every Saturday</option>
                  <option value="7">Every Sunday</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ ...Styles.formGroup, flex: 1 }}>
                  <label style={{ ...Styles.labelStyle, color: '#111111', fontWeight: 'bold' }}>🕒 Start Time</label>
                  <select value={recurringStartTime} onChange={(e) => setRecurringStartTime(e.target.value)} style={{ ...Styles.inputStyle, background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0' }}>
                    {timeLabels.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ ...Styles.formGroup, flex: 1 }}>
                  <label style={{ ...Styles.labelStyle, color: '#111111', fontWeight: 'bold' }}>⏳ End Time</label>
                  <select value={recurringEndTime} onChange={(e) => setRecurringEndTime(e.target.value)} style={{ ...Styles.inputStyle, background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0' }}>
                    {timeLabels.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div style={Styles.formGroup}>
                <label style={{ ...Styles.labelStyle, color: '#111111', fontWeight: 'bold' }}>🔁 Repeat Horizon / Package</label>
                <select value={recurringWeeks} onChange={(e) => setRecurringWeeks(e.target.value)} style={{ ...Styles.inputStyle, background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0' }}>
                  <option value="2">2 Weeks Package</option>
                  <option value="3">3 Weeks Package</option>
                  <option value="4">4 Weeks (1 Month Plan)</option>
                  <option value="6">6 Weeks Intensive</option>
                  <option value="8">8 Weeks (2 Month Plan)</option>
                </select>
              </div>
              <button type="submit" style={{ ...Styles.submitBulkBtn, background: '#0066cc', color: '#ffffff', border: 'none', fontWeight: 'bold' }}>Generate Preview List 📋</button>
            </form>
          </div>

          <div style={{ ...Styles.cardStyle, flex: 1.2, minWidth: '350px', background: '#ffffff', border: '1px solid #cbd5e0' }}>
            <h3 style={{ marginTop: 0, color: '#2f855a' }}>🛒 Review Cart ({stagedBookings.filter(b => b.selected).length} Selected)</h3>
            {stagedBookings.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#4a5568', border: '2px dashed #cbd5e0', borderRadius: '8px', marginTop: '20px' }}>Your staging area is empty. Generate a plan on the left.</div>
            ) : (
              <div>
                <div style={{ maxHeight: '250px', overflowY: 'auto', background: '#f8f9fa', padding: '10px', borderRadius: '8px', marginBottom: '15px' }}>
                  {stagedBookings.map((b) => (
                    <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={b.selected} onChange={() => toggleStagedItemSelection(b.id)} />
                        <span>{b.formattedDate} ({b.time})</span>
                      </label>
                    </div>
                  ))}
                </div>
                <button onClick={commitAllStagedBookings} disabled={isBooking} style={{ background: '#2f855a', color: '#fff', border: 'none', padding: '10px', width: '100%', fontWeight: 'bold', borderRadius: '6px', cursor: 'pointer' }}>Submit Bookings ✅</button>
              </div>
            )}
          </div>
        </div>
      )}

{/* --- TAB 3: SINGLE-PAGE PROGRESS & REPORTING HUB --- */}
      {activeMainTab === 'hub' && (() => {
        const studentFramework = DYNAMIC_VEHICLE_FRAMEWORKS[studentProfile?.training_category || "Car (Manual)"] || {};
        const totalChecklist = MASTER_READINESS_CHECKLIST.length;
        const achievedChecklist = (studentProfile?.test_readiness_checklist || []).length;
        const checklistPercent = Math.round((achievedChecklist / totalChecklist) * 100) || 0;
        const overallReadiness = Math.round((progressPercent + checklistPercent) / 2);
        
        const loggedMinutes = studentHistoricalLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
        const loggedHours = loggedMinutes / 60;
        const priorHours = Number(studentProfile?.prior_hours_experience) || 0;
        const totalHoursDriven = (priorHours + loggedHours).toFixed(1);

        let statusBadgeColor = '#e53e3e';
        let statusText = 'Foundation Stage (Needs Development)';
        if (overallReadiness >= 80) { statusBadgeColor = '#22c55e'; statusText = '🌟 Test Ready / Exam Standard'; } 
        else if (overallReadiness >= 50) { statusBadgeColor = '#eab308'; statusText = '📈 Intermediate Progress'; }

        const handlePrintStudentReport = () => {
            let competencyHtml = '';
            Object.entries(studentFramework).forEach(([category, skills]) => {
                const totalPossible = skills.length * 5;
                const totalEarned = skills.reduce((sum, skill) => sum + (studentProfile?.skills?.[skill] || 1), 0);
                const catPercent = Math.round((totalEarned / totalPossible) * 100);
                const catBarColor = catPercent >= 80 ? '#22c55e' : catPercent >= 50 ? '#eab308' : '#ef4444';

                let subSkillsHtml = '';
                skills.forEach(skill => {
                    const score = studentProfile?.skills?.[skill] || 1;
                    const subPercent = Math.round((score / 5) * 100);
                    const subBarColor = score === 5 ? '#22c55e' : score >= 3 ? '#0066cc' : '#ef4444';

                    subSkillsHtml += `
                        <div style="margin-top: 4px; background: #ffffff; padding: 4px 6px; border: 1px solid #e2e8f0; border-radius: 3px;">
                            <div style="display: flex; justify-content: space-between; font-size: 9px; font-weight: 600; margin-bottom: 2px; color: #334155;">
                                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 110px;" title="${skill}">${skill}</span>
                                <span style="color: ${subBarColor};">${score}/5</span>
                            </div>
                            <div style="width: 100%; height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden;">
                                <div style="width: ${subPercent}%; height: 100%; background: ${subBarColor};"></div>
                            </div>
                        </div>
                    `;
                });

                competencyHtml += `
                    <div style="background: #f8f9fa; padding: 10px; border: 1px solid #cbd5e0; border-radius: 6px; break-inside: avoid; page-break-inside: avoid;">
                        <div style="display: flex; justify-content: space-between; font-size: 11px; font-weight: bold; margin-bottom: 4px; color: #1e293b;">
                            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 130px;" title="${category}">${category}</span>
                            <span style="color: ${catBarColor};">${catPercent}%</span>
                        </div>
                        <div style="width: 100%; height: 6px; background: #e2e8f0; border-radius: 3px; overflow: hidden; margin-bottom: 6px; border: 1px solid #cbd5e0;">
                            <div style="width: ${catPercent}%; height: 100%; background: ${catBarColor};"></div>
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 2px;">
                            ${subSkillsHtml}
                        </div>
                    </div>
                `;
            });

            const printWindow = window.open('', '_blank');
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Student Progress Summary - ${learnerName}</title>
                    <style>
                        body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; padding: 15px; line-height: 1.3; max-width: 1000px; margin: 0 auto; background: #fff; }
                        .report-header { display: flex; justify-content: space-between; border-bottom: 2px solid #0066cc; padding-bottom: 8px; margin-bottom: 15px; }
                        h1 { color: #0066cc; margin: 0 0 2px 0; font-size: 18px; }
                        .card { background: #f8f9fa; border: 1px solid #cbd5e0; border-radius: 6px; padding: 12px; margin-bottom: 12px; }
                        .meta-grid { display: flex; gap: 10px; font-size: 11px; margin-top: 6px; }
                        .meta-item { flex: 1; background: #ffffff; padding: 8px; border-radius: 4px; border: 1px solid #e2e8f0; text-align: center; }
                        .competency-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
                        .footer { margin-top: 20px; text-align: center; font-size: 9px; color: #64748b; border-top: 1px solid #cbd5e0; padding-top: 6px; }
                        @media print { 
                            body { padding: 5px; } 
                            button { display: none; } 
                            @page { size: A4 portrait; margin: 10mm; }
                        }
                    </style>
                </head>
                <body>
                    <div class="report-header">
                        <div>
                            <h1>🚗 Independent Driving School</h1>
                            <p style="margin: 0; color: #64748b; font-size: 11px;">Official Student Training Summary & Performance Report</p>
                        </div>
                        <div style="text-align: right;">
                            <p style="margin: 0; font-weight: bold; font-size: 13px;">${learnerName}</p>
                            <p style="margin: 2px 0 0 0; font-size: 10px; color: #64748b;">Generated: ${new Date().toLocaleDateString('en-GB')}</p>
                        </div>
                    </div>

                    <div class="card" style="padding: 10px 12px; margin-bottom: 10px;">
                        <h3 style="margin: 0 0 6px 0; color: #0066cc; font-size: 13px;">📊 Training Status & Volume Overview</h3>
                        <div class="meta-grid">
                            <div class="meta-item"><span style="color: #64748b; font-weight: bold; display: block; font-size: 8px; margin-bottom: 2px;">CATEGORY</span><strong>${studentProfile?.training_category || "Car (Manual)"}</strong></div>
                            <div class="meta-item"><span style="color: #64748b; font-weight: bold; display: block; font-size: 8px; margin-bottom: 2px;">TOTAL DRIVEN</span><strong style="color: #22c55e;">${totalHoursDriven} Hours</strong></div>
                            <div class="meta-item"><span style="color: #64748b; font-weight: bold; display: block; font-size: 8px; margin-bottom: 2px;">MASTERY</span><strong style="color: #0066cc;">${progressPercent}%</strong></div>
                        </div>
                    </div>

                    <div class="card" style="margin-bottom: 10px;">
                        <h3 style="margin: 0 0 10px 0; color: #0066cc; font-size: 13px;">📁 Detailed Core Competency & Sub-Skill Breakdown</h3>
                        <div class="competency-grid">
                            ${competencyHtml}
                        </div>
                    </div>

                    <div class="footer"><p>Independent Driving School Management Platform — Student Progress Report</p></div>
                    <script>window.onload = function() { window.print(); }</script>
                </body>
                </html>
            `;
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            printWindow.focus();
        };

        const selectedLog = studentHistoricalLogs.find(l => String(l.id) === String(selectedAuditLogId)) || studentHistoricalLogs[0];

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1000px', margin: '20px auto', width: '100%', padding: '0 15px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={handlePrintStudentReport} style={{ background: '#0066cc', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                🖨️ Print Progress Report
              </button>
            </div>

            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px' }}>🕒</span>
                    <h4 style={{ margin: 0, color: '#0066cc', fontSize: '15px' }}>Total Training Volume Completed</h4>
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}>
                    <b>{totalHoursDriven} Hours Total</b> ({priorHours} hrs prior experience + {loggedHours.toFixed(1)} hrs recorded across {studentHistoricalLogs.length} sessions).
                  </p>
                </div>
                <div>
                  <span style={{ padding: '6px 12px', background: statusBadgeColor, color: '#ffffff', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>{statusText}</span>
                </div>
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#0066cc' }}>📊 Visual Test Readiness & Exam Proximity</h4>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  <span>Syllabus Competency Mastery</span>
                  <span>{progressPercent}%</span>
                </div>
                <div style={{ width: '100%', height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden', border: '1px solid #cbd5e0' }}>
                  <div style={{ height: '100%', width: `${progressPercent}%`, background: progressPercent >= 75 ? '#2f855a' : '#0066cc' }}></div>
                </div>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  <span>Test-Readiness Gate ({achievedChecklist} / {totalChecklist} Requisites Met)</span>
                  <span>{checklistPercent}%</span>
                </div>
                <div style={{ width: '100%', height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden', border: '1px solid #cbd5e0' }}>
                  <div style={{ height: '100%', width: `${checklistPercent}%`, background: checklistPercent >= 80 ? '#2f855a' : '#d69e2e' }}></div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', alignItems: 'start' }}>
              <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0' }}>
                <h3 style={{ margin: '0 0 5px 0', color: '#0066cc', fontSize: '15px', textAlign: 'center' }}>📁 6 Core Competency Category Breakdowns</h3>
                <p style={{ fontSize: '11px', color: '#4a5568', textAlign: 'center', marginBottom: '10px' }}>Click any category card to expand/collapse sub-skills.</p>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {Object.entries(studentFramework).map(([category, skills]) => {
                      const totalPossible = skills.length * 5;
                      const totalEarned = skills.reduce((sum, skill) => sum + (studentProfile?.skills?.[skill] || 1), 0);
                      const catPercent = Math.round((totalEarned / totalPossible) * 100);
                      const isExpanded = expandedCategory === category;

                      return (
                          <div 
                            key={category} 
                            onClick={() => setExpandedCategory(isExpanded ? null : category)}
                            style={{ background: '#f8f9fa', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '12px 6px', textAlign: 'center', cursor: 'pointer' }}
                          >
                              <div style={{ position: 'relative', width: '70px', height: '70px', margin: '0 auto 6px auto' }}>
                                  <div dangerouslySetInnerHTML={{ __html: getDonutSvg(catPercent, 70, 7) }} />
                                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontWeight: 'bold', fontSize: '12px', color: '#1e293b' }}>
                                    {catPercent}%
                                  </div>
                              </div>
                              <strong style={{ fontSize: '11px', color: '#1e293b', display: 'block', fontWeight: '600', lineHeight: '1.2' }}>{category}</strong>
                              <span style={{ fontSize: '9px', color: '#0066cc', fontWeight: 'bold', display: 'block', marginTop: '4px' }}>{isExpanded ? '▲ Hide' : '▼ Inspect'}</span>

                              {isExpanded && (
                                <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #cbd5e0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={e => e.stopPropagation()}>
                                  {skills.map(skill => {
                                    const score = studentProfile?.skills?.[skill] || 1;
                                    let badgeColor = score === 5 ? '#22c55e' : score >= 3 ? '#0066cc' : '#ef4444';
                                    return (
                                      <div key={skill} style={{ background: '#ffffff', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontWeight: '600' }}>
                                          <span>{skill}</span>
                                          <span style={{ color: badgeColor }}>{score}/5</span>
                                        </div>
                                        <div style={{ width: '100%', height: '3px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                                          <div style={{ width: `${(score / 5) * 100}%`, height: '100%', background: badgeColor }}></div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                      );
                  })}
                </div>
              </div>

              <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0' }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#2f855a' }}>📋 Core Requisites Checklist</h4>
                <p style={{ fontSize: '11px', color: '#4a5568', marginBottom: '12px' }}>Instructor milestone gates.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '380px', overflowY: 'auto', paddingRight: '4px' }}>
                  {MASTER_READINESS_CHECKLIST.map(item => {
                    const ready = (studentProfile?.test_readiness_checklist || []).includes(item);
                    return (
                      <div key={item} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#ffffff', borderRadius: '6px', border: ready ? '1px solid #2f855a' : '1px solid #cbd5e0', alignItems: 'center', gap: '5px' }}>
                        <span style={{ fontSize: '11px', color: '#111111', fontWeight: '500' }}>{item}</span>
                        <span style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{ready ? '✅ Done' : '⏳ Pending'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                <h4 style={{ margin: 0, color: '#0066cc' }}>📜 Select Past Lesson by Date</h4>
                {studentHistoricalLogs.length > 0 && (
                  <select 
                    value={selectedAuditLogId} 
                    onChange={e => setSelectedAuditLogId(e.target.value)}
                    style={{ ...Styles.selectStyle, background: '#ffffff', fontWeight: 'bold', color: '#0066cc', border: '1px solid #cbd5e0', padding: '8px 12px' }}
                  >
                    {studentHistoricalLogs.map(log => {
                      const dStr = log.lesson_date ? new Date(log.lesson_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Session';
                      return <option key={log.id} value={log.id}>📅 {dStr}</option>;
                    })}
                  </select>
                )}
              </div>

              {studentHistoricalLogs.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#4a5568', border: '1px dashed #cbd5e0', borderRadius: '8px' }}>No session histories filed yet.</div>
              ) : selectedLog ? (
                <div style={{ background: '#ffffff', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '12px', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0066cc' }}>
                      📅 Lesson Date: {new Date(selectedLog.lesson_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span style={{ fontSize: '12px', padding: '4px 12px', background: '#d69e2e', color: '#ffffff', borderRadius: '6px', fontWeight: 'bold' }}>
                      Grade: {selectedLog.progress_score}/5
                    </span>
                  </div>
                  {selectedLog.custom_notes && <p style={{ background: '#f8f9fa', padding: '10px', borderRadius: '6px', margin: '0 0 10px 0', fontSize: '13px' }}><b>Summary:</b> {selectedLog.custom_notes}</p>}
                  {selectedLog.next_steps && <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '6px', color: '#166534', fontSize: '13px' }}><b>Next Steps:</b> {selectedLog.next_steps}</div>}
                </div>
              ) : null}
            </div>

          </div>
        );
      })()}

      {/* --- SETTINGS MODAL --- */}
      {showSettings && (
        <div style={Styles.modalOverlay}>
          <div style={{ ...Styles.modalContent, width: '550px', maxHeight: '90vh', overflowY: 'auto', background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0' }}>
            <h2 style={{ color: '#0066cc', marginTop: 0 }}>Account Settings</h2>
            <StudentOnboarding studentId={learnerId} onComplete={() => { setShowSettings(false); fetchScheduleData(); }} />
            <button onClick={() => setShowSettings(false)} style={{ ...Styles.actionBtn, background: '#cbd5e0', color: '#111111', width: '100%', marginTop: '10px', border: 'none', fontWeight: 'bold' }}>Close Settings</button>
          </div>
        </div>
      )}

      {/* --- LEARNER HELP MODAL --- */}
      <LearnerHelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />

    </div>
  ); 
} 

export default LearnerDash;